import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';
import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { IS_TMA } from '@rpgc-ai/environments';

import { TranslocoService } from '@jsverse/transloco';
import { filter, tap } from 'rxjs';

import {
  AUTH_CALLBACK_QUERY_PARAM,
  AUTH_CALLBACK_QUERY_VALUE,
  MAGIC_LINK_ERROR_QUERY_PARAM,
  MAGIC_LINK_ERROR_QUERY_VALUE,
} from './auth-callback.constants';

import { NotificationsService } from '../notifications/notifications.service';

/**
 * Реагирует на ошибки auth-flows после deeplink-возврата:
 * - Legacy `?magicLinkError=1` (web): исторический маркер от старого фронта.
 *   Сейчас web всё ещё передаёт `errorCallbackURL` с этим query в magic-link
 *   запросе → бакенд редиректит сюда при verify failure.
 * - TMA-only defensive `?authCallback=1&error=<code>`: бакенд wrap'ает callbackURL
 *   в `t.me`-deeplink, и при verify/OAuth failure добавляет `error` query. TMA
 *   передаёт ТОЛЬКО callbackURL (без errorCallbackURL), поэтому этот pattern
 *   срабатывает только при TMA-возврате.
 *
 * Показывает notification и чистит соответствующие query-параметры из URL.
 */
@Injectable({ providedIn: 'root' })
export class MagicLinkErrorRedirectService {
  readonly #router = inject(Router);
  readonly #notifications = inject(NotificationsService);
  readonly #transloco = inject(TranslocoService);
  readonly #isServer = inject(IS_SERVER);
  readonly #window = inject(WINDOW);
  readonly #destroyRef = inject(DestroyRef);
  readonly #isTma = inject(IS_TMA);

  init(): void {
    if (this.#isServer || !this.#window) {
      return;
    }

    this.#router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        tap(() => {
          this.#consumeLegacyMagicLinkErrorQuery();
          if (this.#isTma) {
            this.#consumeAuthCallbackErrorQuery();
          }
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #consumeLegacyMagicLinkErrorQuery(): void {
    const url = new URL(this.#router.url, `${this.#window?.location.origin}/`);
    if (url.searchParams.get(MAGIC_LINK_ERROR_QUERY_PARAM) !== MAGIC_LINK_ERROR_QUERY_VALUE) {
      return;
    }

    this.#notifications.addErrorNotification(this.#transloco.translate('MODALS.MAGIC_LINK.ERROR'));

    url.searchParams.delete(MAGIC_LINK_ERROR_QUERY_PARAM);
    void this.#router.navigateByUrl(`${url.pathname}${url.search}${url.hash}`, { replaceUrl: true });
  }

  /**
   * Defensive: `?authCallback=1&error=<code>` — better-auth append'нул ошибку
   * к нашему callbackURL. Показываем generic notification, удаляем `error`
   * и `authCallback` чтобы downstream session.service не считал это успешным
   * authCallback.
   */
  #consumeAuthCallbackErrorQuery(): void {
    const url = new URL(this.#router.url, `${this.#window?.location.origin}/`);

    const isAuthCallback = url.searchParams.get(AUTH_CALLBACK_QUERY_PARAM) === AUTH_CALLBACK_QUERY_VALUE;
    const errorCode = url.searchParams.get('error');

    if (!isAuthCallback || !errorCode) {
      return;
    }

    this.#notifications.addErrorNotification(this.#transloco.translate('MODALS.MAGIC_LINK.ERROR'));

    url.searchParams.delete('error');
    url.searchParams.delete(AUTH_CALLBACK_QUERY_PARAM);
    void this.#router.navigateByUrl(`${url.pathname}${url.search}${url.hash}`, { replaceUrl: true });
  }
}
